import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

// Types based on API documentation
export interface RedisKeyValueDto {
  key: string;
  type: 'string' | 'hash' | 'list' | 'set' | 'zset' | 'stream' | 'none';
  value: any;
  ttl: number; // -1 = no expiry, -2 = key does not exist, positive = seconds until expiry
  size?: number; // Approximate size in bytes
}

export interface RedisKeysResponse {
  keys: RedisKeyValueDto[];
  cursor: string;
  hasMore: boolean;
  totalScanned: number;
  pattern: string;
}

export interface RedisStatsDto {
  keyCount: number;
  memoryUsage: string;
  memoryUsageBytes: number;
  connectedClients: number;
  uptimeSeconds: number;
  version: string;
  usedMemoryPeak: string;
  evictedKeys: number;
  keyspaceHits: number;
  keyspaceMisses: number;
  totalCommandsProcessed: number;
}

export interface CollectionTimestamps {
  [collection: string]: string;
}

export interface ClearCacheRequest {
  pattern: string;
  confirmClear: boolean;
  reason?: string;
}

export interface ClearCacheResponse {
  deletedCount: number;
  pattern: string;
  timestamp: string;
  clearedBy?: string;
  reason?: string;
}

class CacheStorageService {
  /**
   * Get collection last updated timestamps
   */
  async getCollectionTimestamps(): Promise<CollectionTimestamps | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CACHE_STORAGE.COLLECTION_TIMESTAMPS);
      
      if (response.data.success) {
        return response.data.data?.data || null;
      }
      
      throw new Error(response.data.error?.error_description || 'Failed to fetch collection timestamps');
    } catch (error) {
      console.error('Error fetching collection timestamps:', error);
      throw error;
    }
  }

  /**
   * Get Redis keys with pagination
   */
  async getRedisKeys(pattern: string = '*', limit: number = 100, cursor: string = '0'): Promise<RedisKeysResponse> {
    try {
      const params = new URLSearchParams({
        pattern,
        limit: limit.toString(),
        cursor
      });
      
      const response = await apiClient.get(`${API_ENDPOINTS.CACHE_STORAGE.KEYS}?${params.toString()}`);
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error?.error_description || 'Failed to fetch Redis keys');
    } catch (error) {
      console.error('Error fetching Redis keys:', error);
      throw error;
    }
  }

  /**
   * Get all keys with automatic pagination
   */
  async getAllKeys(pattern: string = '*'): Promise<RedisKeyValueDto[]> {
    const allKeys: RedisKeyValueDto[] = [];
    let cursor = '0';
    let iterations = 0;
    const maxIterations = 100; // Safety limit
    
    try {
      do {
        const result = await this.getRedisKeys(pattern, 100, cursor);
        allKeys.push(...result.keys);
        cursor = result.cursor;
        iterations++;
        
        if (!result.hasMore || iterations >= maxIterations) {
          break;
        }
      } while (cursor !== '0');
      
      return allKeys;
    } catch (error) {
      console.error('Error fetching all keys:', error);
      throw error;
    }
  }

  /**
   * Clear Redis cache by pattern
   */
  async clearCache(pattern: string, reason?: string): Promise<ClearCacheResponse> {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.CACHE_STORAGE.KEYS, {
        data: {
          pattern,
          confirmClear: true,
          reason
        }
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error?.error_description || 'Failed to clear cache');
    } catch (error) {
      console.error('Error clearing cache:', error);
      throw error;
    }
  }

  /**
   * Get Redis server statistics
   */
  async getStats(): Promise<RedisStatsDto> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CACHE_STORAGE.STATS);
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error?.error_description || 'Failed to fetch Redis stats');
    } catch (error) {
      console.error('Error fetching Redis stats:', error);
      throw error;
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Calculate cache hit ratio
   */
  calculateHitRatio(hits: number, misses: number): number {
    const total = hits + misses;
    if (total === 0) return 0;
    return (hits / total) * 100;
  }

  /**
   * Format bytes to human readable
   */
  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Format uptime to human readable
   */
  formatUptime(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    
    return parts.join(' ') || '0m';
  }

  /**
   * Parse TTL to human readable
   */
  formatTTL(ttl: number): string {
    if (ttl === -1) return 'No expiry';
    if (ttl === -2) return 'Key does not exist';
    if (ttl === 0) return 'Expired';
    
    if (ttl < 60) return `${ttl}s`;
    if (ttl < 3600) return `${Math.floor(ttl / 60)}m`;
    if (ttl < 86400) return `${Math.floor(ttl / 3600)}h`;
    return `${Math.floor(ttl / 86400)}d`;
  }

  /**
   * Get key type color for UI
   */
  getTypeColor(type: string): string {
    const colors: Record<string, string> = {
      string: 'text-blue-600 bg-blue-50',
      hash: 'text-green-600 bg-green-50',
      list: 'text-purple-600 bg-purple-50',
      set: 'text-orange-600 bg-orange-50',
      zset: 'text-pink-600 bg-pink-50',
      stream: 'text-indigo-600 bg-indigo-50',
      none: 'text-gray-600 bg-gray-50'
    };
    return colors[type] || 'text-gray-600 bg-gray-50';
  }

  /**
   * Group keys by prefix
   */
  groupKeysByPrefix(keys: RedisKeyValueDto[]): Record<string, RedisKeyValueDto[]> {
    const groups: Record<string, RedisKeyValueDto[]> = {};
    
    keys.forEach(key => {
      const prefix = key.key.split('_')[0] || 'OTHER';
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(key);
    });
    
    return groups;
  }
}

// Export singleton instance
export const cacheStorageService = new CacheStorageService();
export default cacheStorageService;